import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import FbButton from 'components/FbUI/FbButton'
import { TextInput } from 'components/FormUtils/TextInput'
import Select from 'components/FormUtils/Select'
import { Modal } from 'components/UI/Modal/Modal'
import { useAuth } from 'context/authentication/useAuth'
import { useRSMEligibleUsers } from 'hooks/useRSMEligibleUsers'
import { CompanyEmailExtension } from 'models/companies'
import { User, USER_TYPE_LIMITED } from 'models/user'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import { z } from 'zod'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'

export interface InviteUserModalProps {
  existingUsers: User[]
}

const createEmailSchema = (
  extensions: CompanyEmailExtension[] = [],
  users: User[]
) =>
  z.object({
    email: z
      .string()
      .email()
      .refine(
        (email) =>
          extensions.find((extension) =>
            email.endsWith(extension.email_extension)
          ),
        {
          message: `Email must match your team's work email (${extensions.map((extension) => extension.email_extension).join(', ')})`,
        }
      )
      .refine((email) => !users.find((u) => u.email === email), {
        message: 'User is already part of your team',
      }),
    user_type: z.string().optional(),
    rsm_assignment: z.number().nullable().optional(),
  })

export function InviteUserModal({ existingUsers = [] }: InviteUserModalProps) {
  const api = useApiService()
  const queryClient = useQueryClient()
  const { isAdmin } = useAuth()
  const { enableUserTypes } = useFeatureFlagService()
  const { data: companyDomains } = api.useGetCompanyEmailExtensions()
  const [showModal, setShowModal] = useState<boolean>(false)

  const { data: userTypes } = useQuery({
    queryKey: ['user-types'],
    queryFn: async () => {
      return await api.getUserTypes()
    },
    enabled: enableUserTypes && isAdmin,
  })

  const formSchema = useMemo(
    () => createEmailSchema(companyDomains, existingUsers),
    [companyDomains, existingUsers]
  )

  type FormSchema = z.infer<typeof formSchema>

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    reset,
    watch,
  } = useForm<FormSchema>({
    defaultValues: {
      email: '',
      user_type: 'Standard',
      rsm_assignment: null,
    },
    resolver: zodResolver(formSchema),
  })

  const selectedType = watch('user_type')
  const selectedRSM = watch('rsm_assignment')
  const isLimitedType = selectedType === USER_TYPE_LIMITED

  const { rsmOptions } = useRSMEligibleUsers(
    enableUserTypes && isAdmin && isLimitedType
  )

  const blockAccept = isLimitedType && !selectedRSM

  function openModal() {
    setShowModal(true)
  }

  function hideModal() {
    setShowModal(false)
    reset()
  }

  const onSubmit = handleSubmit(async (values) => {
    await api.inviteUser(
      values.email,
      enableUserTypes ? values.user_type : undefined,
      enableUserTypes && isLimitedType ? values.rsm_assignment : undefined
    )
    toast.success('Invite sent successfully!')
    queryClient.invalidateQueries({ queryKey: ['invitations'] })
    hideModal()
  })

  return (
    <>
      <FbButton onClick={() => openModal()}>Invite User</FbButton>
      <Modal
        open={showModal}
        title={'Invite User'}
        description={
          "Enter your teammate's work email to invite them to join you on First Bite."
        }
        onOpenChange={(open) => !open && hideModal()}
        loading={isSubmitting}
        blockAccept={blockAccept}
        onAccept={onSubmit}
        acceptButtonText={'Invite'}
      >
        <TextInput
          name="email"
          control={control}
          type="text"
          label={'Work Email'}
          placeholder="name@company.com"
        />

        {enableUserTypes && isAdmin && (
          <>
            <Select
              control={control}
              name="user_type"
              label="User Type"
              options={userTypes ?? []}
              isClearable={false}
            />

            {isLimitedType && (
              <Select
                control={control}
                name="rsm_assignment"
                label="Assigned Sample Reviewer"
                options={rsmOptions}
                isClearable={false}
                placeholder="Select a sample reviewer..."
              />
            )}
          </>
        )}
      </Modal>
    </>
  )
}
